/**
 * Ce que le tunnel de commande lit et écrit, en un seul endroit.
 *
 * Deux fonctions, pas une de plus : chiffrer un panier, et le commander. Les
 * deux passent par `supabase.rpc()` — jamais par un `fetch` sur une URL
 * assemblée à la main, qui produirait `undefined/functions/v1/...` dans le
 * bundle si `VITE_SUPABASE_URL` manquait à la compilation, et n'en dirait rien
 * avant le clic du parent.
 *
 * ## Le panier n'envoie que des identifiants
 *
 * `lignes` ne porte que `(session_id, kid_id)`. Aucun prix, aucun type de
 * tarif. C'est le serveur qui les décide, et c'est tout l'objet de cette
 * réécriture : l'ancien `create-invoice` écrivait
 *
 *     amount_paid: priceInEuros,   // = item.price / 100, venu du NAVIGATEUR
 *
 * ce que le navigateur lui donnait.
 */

import { supabase } from '../supabase';
import { messageAffichable, type Commande, type Devis } from './devis';
import {
  produireEtEnvoyer,
  type EnvoiDeFacture,
  type OptionsDeFacture,
} from './facture';

export type { EnvoiDeFacture, OptionsDeFacture, OutilsDeFacture } from './facture';

/** Une ligne de panier, telle qu'elle part vers la base. */
export interface LigneCommande {
  session_id: string;
  kid_id: string;
}

/** La déclaration sur l'honneur, telle qu'elle sera conservée. */
export interface DeclarationEnvoyee {
  threshold_cents: number;
  statement_text: string;
}

/*
  Une erreur portant la phrase à afficher. Les fonctions de la base rendent des
  messages français destinés au parent ; `messageAffichable` écarte ceux qui
  sont techniques.
*/
export class ErreurDeCommande extends Error {
  readonly brut: string;
  constructor(brut: string | null | undefined) {
    super(messageAffichable(brut));
    this.brut = (brut ?? '').trim();
    this.name = 'ErreurDeCommande';
  }
}

/**
 * Chiffre le panier sans rien écrire. Appelable à chaque changement : la
 * fonction est STABLE et ne prend aucun verrou.
 *
 * Elle ne vérifie ni la capacité ni les doublons — ces contrôles n'ont de sens
 * qu'au moment d'écrire. Une place libre ici peut être prise trois secondes
 * plus tard, et c'est `commander` qui refusera.
 */
export async function chiffrer(
  userId: string,
  lignes: LigneCommande[],
  reduitDeclare: boolean,
  codes: string[],
): Promise<Devis> {
  const { data, error } = await supabase.rpc('quote_order', {
    p_user_id: userId,
    p_lines: lignes,
    p_reduced_declared: reduitDeclare,
    p_gratuity_codes: codes,
  });

  if (error) throw new ErreurDeCommande(error.message);
  if (!data) throw new ErreurDeCommande(null);
  return data as Devis;
}

/**
 * Passe la commande. Une seule transaction : facture, inscriptions, remises et
 * provision, ou rien.
 *
 * `declaration` est obligatoire dès que `reduitDeclare` est vrai — la base
 * refuse une déclaration sur l'honneur dont on n'a pas conservé l'énoncé.
 */
export async function commander(
  userId: string,
  lignes: LigneCommande[],
  reduitDeclare: boolean,
  declaration: DeclarationEnvoyee | null,
  codes: string[],
): Promise<Commande> {
  const { data, error } = await supabase.rpc('create_order', {
    p_user_id: userId,
    p_lines: lignes,
    p_reduced_declared: reduitDeclare,
    p_declaration: reduitDeclare ? declaration : null,
    p_gratuity_codes: codes,
  });

  if (error) throw new ErreurDeCommande(error.message);
  if (!data) throw new ErreurDeCommande(null);
  return data as Commande;
}

/**
 * Le document et le courriel, APRÈS le COMMIT.
 *
 * `create_order` ne les produit pas : un PDF généré dans la transaction
 * survivrait à son annulation, et un courriel encore davantage — on ne rappelle
 * pas un message parti.
 *
 * L'enchaînement est celui de l'ancien tunnel, et il tient en un appel :
 * `send-invoice-email` demande le PDF à `generate-invoice-pdf`, l'enregistre
 * sur la facture, puis l'envoie depuis `stage-notif@eclosion.be` (SMTP_SENDER).
 *
 * **Un échec ici n'annule rien.** L'enfant est inscrit, la facture existe. Le
 * résultat dit ce qui a marché pour que l'écran le montre honnêtement plutôt
 * que de laisser croire qu'un courriel est parti.
 */
export async function envoyerFacture(
  invoiceNumber: string,
  parentEmail: string,
  forcer = false,
): Promise<EnvoiDeFacture> {
  try {
    const { data, error } = await supabase.functions.invoke('send-invoice-email', {
      body: { invoice_number: invoiceNumber, parent_email: parentEmail, force: forcer },
    });
    if (error) return { pdfUrl: null, erreur: error.message };

    const r = data as { pdf_url?: string; already_sent?: boolean } | null;
    const url = r?.pdf_url ?? null;

    /*
      Le webhook Stripe a envoyé la facture avant nous — c'est le cas NORMAL
      quand le parent revient d'un paiement réussi. Ce n'est pas un échec : le
      courriel est parti, et l'annoncer comme un échec ferait cliquer sur
      « réessayer » pour rien. Le PDF peut n'être pas encore écrit sur la
      facture au moment où l'autre émetteur nous devance ; son absence ne dit
      donc rien ici.
    */
    if (r?.already_sent) return { pdfUrl: url, erreur: null };

    return { pdfUrl: url, erreur: url ? null : "La facture n'a pas pu être produite." };
  } catch (e) {
    return { pdfUrl: null, erreur: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Produire le document et l'envoyer, **une fois**, avec les vrais appels.
 *
 * La règle vit dans `facture.ts` — elle n'a besoin d'aucun réseau et s'éprouve
 * seule. Ici on ne fait que lui fournir ses deux collaborateurs, pour qu'aucun
 * écran n'ait à les connaître.
 */
export async function produireEtEnvoyerFacture(
  invoiceNumber: string,
  courriel: string | null,
  options: OptionsDeFacture = {},
): Promise<EnvoiDeFacture> {
  return produireEtEnvoyer(
    invoiceNumber, courriel,
    { lirePdf: pdfDejaProduit, envoyer: envoyerFacture },
    options,
  );
}

/**
 * L'adresse du PDF déjà produit pour cette facture, s'il existe.
 *
 * Sert à ne pas renvoyer un courriel à chaque rechargement de la page de
 * confirmation : un parent qui rafraîchit ne doit pas recevoir trois fois la
 * même facture.
 */
export async function pdfDejaProduit(invoiceNumber: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('invoices')
    .select('pdf_url')
    .eq('invoice_number', invoiceNumber)
    .maybeSingle();
  if (error) return null;
  return (data as { pdf_url?: string } | null)?.pdf_url ?? null;
}
